// AGC: Revenue Detail Panel Component
// AGC: Simplified, elegant panel for revenue breakdown inside hero gradient
// AGC: 3-column table (Lot, Area, Revenue) - no horizontal scroll
// AGC: White/transparent styling for gradient background

import React, { useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronUp,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Wheat,
  ArrowUpRight,
  ArrowDownRight,
  Layers,
  DollarSign,
  AreaChart,
} from 'lucide-react'
import { ChevronRight } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { formatCurrency } from '@droidfarm/shared'
import type { LotProjection } from '../../hooks/useDashboardData'
import type { CropPrices } from '../../services/priceService'
import type { Screen } from '../../types'

// =============================================================================
// AGC: TYPES
// =============================================================================

interface RevenueDetailPanelProps {
  lotProjections: LotProjection[]
  prices: CropPrices
  isExpanded: boolean
  onClose: () => void
  showBalance: boolean
  onNavigate?: (screen: Screen) => void
}

interface CropSummary {
  cropType: string
  totalArea: number
  totalProduction: number
  avgYield: number
  avgPrice: number
  totalRevenue: number
  lotCount: number
}

// =============================================================================
// AGC: HELPER FUNCTIONS
// =============================================================================

// AGC: Format large numbers with K/M suffix
const formatLargeNumber = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`
  }
  return value.toFixed(0)
}

// AGC: Format currency without decimals (whole numbers only)
const formatCurrencyNoDecimals = (value: number): string => {
  return `R$ ${Math.round(value).toLocaleString('pt-BR')}`
}

// =============================================================================
// AGC: SIMPLIFIED LOT BREAKDOWN - 3 COLUMNS ONLY
// =============================================================================

// AGC: Each data row is ~36px; 3 rows = ~108px visible before scroll kicks in
const LOT_ROW_HEIGHT_PX = 37
const MAX_VISIBLE_LOTS = 3

const LotBreakdownSimple: React.FC<{
  projections: LotProjection[]
  showBalance: boolean
  onNavigate?: (screen: Screen) => void
}> = ({ projections, showBalance, onNavigate }) => {
  const { t } = useTranslation()
  const [sortBy, setSortBy] = useState<'revenue' | 'area' | 'name'>('revenue')

  // AGC: Sort projections
  const sortedProjections = useMemo(() => {
    return [...projections].sort((a, b) => {
      switch (sortBy) {
        case 'revenue':
          return b.projectedRevenue - a.projectedRevenue
        case 'area':
          return b.areaHa - a.areaHa
        case 'name':
          return a.lotName.localeCompare(b.lotName)
        default:
          return 0
      }
    })
  }, [projections, sortBy])

  if (projections.length === 0) {
    return (
      <div className="text-center py-6 text-white/60">
        <Wheat className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>{t('dashboard.revenueDetail.noLots')}</p>
      </div>
    )
  }

  return (
    <div>
      {/* AGC: Sort buttons - pill style for gradient bg */}
      <div className="flex gap-2 mb-3">
        {(['revenue', 'area', 'name'] as const).map((key) => (
          <button
            key={key}
            onClick={() => setSortBy(key)}
            className={`px-3 py-1 text-xs rounded-full transition-all ${
              sortBy === key
                ? 'bg-white text-navy-700 font-semibold'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            {t(`dashboard.revenueDetail.sortBy.${key}`)}
          </button>
        ))}
      </div>

      {/* AGC: Simplified 3-column table - no horizontal scroll */}
      <div className="space-y-2">
        {/* AGC: Header row */}
        <div className="grid grid-cols-3 gap-2 text-xs text-white/50 uppercase tracking-wide pb-2 border-b border-white/10">
          <span>{t('dashboard.revenueDetail.lot')}</span>
          <span className="text-center">{t('dashboard.revenueDetail.area')}</span>
          <span className="text-right">{t('dashboard.revenueDetail.revenue')}</span>
        </div>

        {/* AGC: Data rows — fixed height for 3 rows, overflow scrolls additional lots */}
        <div
          className="overflow-y-auto"
          style={{ maxHeight: `${LOT_ROW_HEIGHT_PX * MAX_VISIBLE_LOTS}px` }}
        >
          {sortedProjections.map((lot, index) => (
            <motion.div
              key={lot.lotId}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="grid grid-cols-3 gap-2 py-2 border-b border-white/5 last:border-b-0 items-center"
            >
              <span className="font-medium text-white text-sm truncate">
                {lot.lotName}
              </span>
              <span className="text-center text-white/70 text-sm">
                {lot.areaHa.toFixed(1)} ha
              </span>
              <span className="text-right font-semibold text-white text-sm">
                {showBalance ? formatCurrencyNoDecimals(lot.projectedRevenue) : '••••••'}
              </span>
            </motion.div>
          ))}
        </div>

        {/* AGC: "Ver Tudo" button — only shown when there are more than MAX_VISIBLE_LOTS lots */}
        {sortedProjections.length > MAX_VISIBLE_LOTS && onNavigate && (
          <button
            onClick={() => onNavigate('lotes-list')}
            className="mt-2 w-full flex items-center justify-center gap-1 py-1.5 text-xs font-medium text-white/70 hover:text-white transition-colors rounded-lg hover:bg-white/10"
          >
            {t('dashboard.viewAll')}
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// AGC: PRICE TRENDS - COMPACT CARDS
// =============================================================================

const PriceTrendsCompact: React.FC<{
  prices: CropPrices
  cropTypes: string[]
}> = ({ prices, cropTypes }) => {
  const { t } = useTranslation()

  // AGC: Generate price data with trends
  const chartData = useMemo(() => {
    const data: { crop: string; current: number; previous: number; trend: 'up' | 'down' | 'stable' }[] = []
    
    for (const crop of cropTypes) {
      const priceData = prices[crop]
      if (priceData) {
        // AGC: Simulate previous price (5% variance) for demo
        const variance = (Math.random() - 0.5) * 0.1
        const current = priceData.valor_saca || priceData.preco || 0
        const previous = current * (1 - variance)
        
        data.push({
          crop,
          current,
          previous,
          trend: current > previous ? 'up' : current < previous ? 'down' : 'stable',
        })
      }
    }
    
    return data
  }, [prices, cropTypes])

  if (chartData.length === 0) {
    return (
      <div className="text-center py-4 text-white/60">
        <BarChart3 className="w-6 h-6 mx-auto mb-2 opacity-50" />
        <p className="text-xs">{t('dashboard.revenueDetail.noPrices')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {chartData.map((item, index) => {
        const changePercent = ((item.current - item.previous) / item.previous) * 100
        const isPositive = changePercent > 0
        
        return (
          <motion.div
            key={item.crop}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="flex items-center justify-between p-3 rounded-xl bg-white/10 backdrop-blur-sm"
          >
            <div>
              <p className="text-sm font-semibold text-white capitalize">
                {item.crop}
              </p>
              <p className="text-xs text-white/60">
                R$ {item.current.toFixed(2)}/sc
              </p>
            </div>
            <div className={`flex items-center gap-1 text-sm font-medium ${
              isPositive ? 'text-red-300' : 'text-green-300'
            }`}>
              {isPositive ? (
                <ArrowUpRight className="w-4 h-4" />
              ) : (
                <ArrowDownRight className="w-4 h-4" />
              )}
              <span>{Math.abs(changePercent).toFixed(1)}%</span>
            </div>
          </motion.div>
        )
      })}
    </div>
  )
}

// =============================================================================
// AGC: CROP SUMMARY - COMPACT CARDS
// =============================================================================

const CropSummaryCompact: React.FC<{
  projections: LotProjection[]
  showBalance: boolean
}> = ({ projections, showBalance }) => {
  const { t } = useTranslation()

  // AGC: Aggregate data by crop type
  const cropSummaries = useMemo(() => {
    const summaryMap = new Map<string, CropSummary>()
    
    for (const lot of projections) {
      const existing = summaryMap.get(lot.cropType)
      
      if (existing) {
        existing.totalArea += lot.areaHa
        existing.totalProduction += lot.totalProduction
        existing.totalRevenue += lot.projectedRevenue
        existing.lotCount += 1
        existing.avgYield = existing.totalProduction / existing.totalArea
        existing.avgPrice = existing.totalRevenue / existing.totalProduction
      } else {
        summaryMap.set(lot.cropType, {
          cropType: lot.cropType,
          totalArea: lot.areaHa,
          totalProduction: lot.totalProduction,
          avgYield: lot.projectedYield,
          avgPrice: lot.pricePerUnit,
          totalRevenue: lot.projectedRevenue,
          lotCount: 1,
        })
      }
    }
    
    return Array.from(summaryMap.values()).sort((a, b) => b.totalRevenue - a.totalRevenue)
  }, [projections])

  if (cropSummaries.length === 0) {
    return null
  }

  return (
    <div className="space-y-2">
      {cropSummaries.map((summary, index) => (
        <motion.div
          key={summary.cropType}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="p-3 rounded-xl bg-white/10 backdrop-blur-sm"
        >
          <div className="flex items-center justify-between mb-2">
            <div>
              <h4 className="font-semibold text-white capitalize">
                {summary.cropType}
              </h4>
              <p className="text-xs text-white/60">
                {summary.lotCount} {summary.lotCount === 1 ? t('dashboard.revenueDetail.lot') : t('dashboard.revenueDetail.lots')} • {summary.totalArea.toFixed(1)} ha
              </p>
            </div>
            <div className="text-right">
              <p className="font-bold text-white">
                {showBalance ? formatCurrencyNoDecimals(summary.totalRevenue) : '••••••'}
              </p>
            </div>
          </div>
          
          {/* AGC: Compact stats row */}
          <div className="grid grid-cols-3 gap-2 text-xs pt-2 border-t border-white/10">
            <div className="text-center">
              <p className="text-white/50">{t('dashboard.revenueDetail.yield')}</p>
              <p className="font-medium text-white">
                {summary.avgYield.toFixed(1)} sc/ha
              </p>
            </div>
            <div className="text-center">
              <p className="text-white/50">{t('dashboard.revenueDetail.price')}</p>
              <p className="font-medium text-white">
                R$ {summary.avgPrice.toFixed(2)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-white/50">{t('dashboard.revenueDetail.total')}</p>
              <p className="font-medium text-white">
                {formatLargeNumber(summary.totalProduction)} sc
              </p>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  )
}

// =============================================================================
// AGC: MAIN COMPONENT
// =============================================================================

export const RevenueDetailPanel: React.FC<RevenueDetailPanelProps> = ({
  lotProjections,
  prices,
  isExpanded,
  onClose,
  showBalance,
  onNavigate,
}) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<'breakdown' | 'prices' | 'yields'>('breakdown')

  // AGC: Get unique crop types
  const cropTypes = useMemo(() => {
    return [...new Set(lotProjections.map(lot => lot.cropType))]
  }, [lotProjections])

  // AGC: Tab configuration with icons
  const tabs = [
    { id: 'breakdown' as const, icon: Layers, label: t('dashboard.revenueDetail.tabs.breakdown') },
    { id: 'prices' as const, icon: DollarSign, label: t('dashboard.revenueDetail.tabs.prices') },
    { id: 'yields' as const, icon: AreaChart, label: t('dashboard.revenueDetail.tabs.yields') },
  ]

  return (
    <AnimatePresence>
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <div className="mt-4 pt-4 border-t border-white/10">
            {/* AGC: Header with close button */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-white">
                {t('dashboard.revenueDetail.title')}
              </h3>
              <button
                onClick={onClose}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
              >
                <ChevronUp className="w-4 h-4 text-white/70" />
              </button>
            </div>

            {/* AGC: Elegant Tab Navigation - Icon pills */}
            <div className="flex gap-1 mb-4 p-1 rounded-xl bg-white/5">
              {tabs.map((tab) => {
                const Icon = tab.icon
                const isActive = activeTab === tab.id
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 text-xs font-medium rounded-lg transition-all ${
                      isActive
                        ? 'bg-white text-navy-700 shadow-sm'
                        : 'text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                )
              })}
            </div>

            {/* AGC: Tab Content - Semi-transparent container */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4">
              <AnimatePresence mode="wait">
                {activeTab === 'breakdown' && (
                  <motion.div
                    key="breakdown"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <LotBreakdownSimple projections={lotProjections} showBalance={showBalance} onNavigate={onNavigate} />
                  </motion.div>
                )}

                {activeTab === 'prices' && (
                  <motion.div
                    key="prices"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <PriceTrendsCompact prices={prices} cropTypes={cropTypes} />
                  </motion.div>
                )}

                {activeTab === 'yields' && (
                  <motion.div
                    key="yields"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <CropSummaryCompact projections={lotProjections} showBalance={showBalance} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default RevenueDetailPanel
